/**
 * Go-live checklist (go-live-checklist.md §B "Final tests" + §C "Go-live") — the single
 * pre-launch source of truth, built into the flow so nothing is a forgotten task ("favicon +
 * social card" were both missed historically). This is the human-VERIFY list (the by-construction
 * items — favicon/OG/404/schema/sitemap — are guaranteed by the template + grounding, bucket A,
 * not here). Mirrors ResponsiveChecklist: tri-state per item, persisted in wizard_state.golive,
 * progress badge, and a SOFT gate on Go-live (confirm on open items). v1 = manual ticks.
 *
 * ⚠️ The 15 items are hardcoded here verbatim from the relay (relay-go-live-checklist-ui.md) —
 * this module is the commented source. Items 1 ("responsive matrix") and 9 ("client approved")
 * are DERIVED read-only roll-ups of existing state (the ResponsiveChecklist + the client_approved
 * gate), never stored in wizard_state.golive — we reflect them, we don't duplicate them.
 */

/** A manual item is open (absent), done, or N/A. Derived items are not stored (see GoLiveRollups). */
export type GoLiveState = 'done' | 'na';
/** wizard_state.golive — only the MANUAL items; 'open' = key absent. */
export type GoLiveMarks = Record<string, GoLiveState>;

export interface GoLiveItem {
  id: string;
  label: string;
  /** 'final' = shown at Preview & approve; 'launch' = the Go-live step's publish actions. */
  phase: 'final' | 'launch';
  /** Read-only roll-up of existing state — reflected, not stored or manually ticked. */
  derived?: 'responsive' | 'approved';
  /** A documented reason this item can legitimately be N/A (shown as a hint). */
  naHint?: string;
}

// Verbatim from go-live-checklist.md §B/§C via the relay. IDs are stable (persisted keys) — do
// not rename. Order = checklist order (final tests, then launch).
export const GOLIVE_ITEMS: GoLiveItem[] = [
  // §B — Final tests (Preview & approve)
  { id: 'responsive', phase: 'final', derived: 'responsive', label: 'Responsive matrix passed' },
  { id: 'links', phase: 'final', label: 'Link audit — every internal / external / social link works, zero 404s' },
  { id: 'placeholders', phase: 'final', label: 'No placeholders left — no lorem / coming soon / TBC / example.com / {{…}}' },
  { id: 'business', phase: 'final', label: 'Business details correct + click-tested — phone (tel:), email (mailto:), map, hours' },
  { id: 'favicon', phase: 'final', label: 'Favicon shows in the browser tab' },
  { id: 'social_card', phase: 'final', label: 'Social share card renders — 1200×630, checked in a preview inspector' },
  { id: 'forms', phase: 'final', label: 'Forms submit + confirmation email lands', naHint: 'N/A if the site has no form' },
  { id: 'pagespeed', phase: 'final', label: 'PageSpeed / Core Web Vitals spot-check (LCP/CLS)' },
  { id: 'approved', phase: 'final', derived: 'approved', label: 'Client approved the preview' },
  // §C — Go-live launch (Go live step)
  { id: 'astro_site', phase: 'launch', label: 'astro.config site = the real domain' },
  { id: 'redirects', phase: 'launch', label: '301 redirect map in place', naHint: 'N/A for a brand-new domain with no old URLs' },
  { id: 'dns', phase: 'launch', label: 'DNS cutover — web resolves to Cloudflare; email verified (SPF/DKIM/DMARC)' },
  { id: 'dnssec_ssl', phase: 'launch', label: 'DNSSEC + SSL active' },
  { id: 'analytics', phase: 'launch', label: 'Analytics instrumented — Plausible + GSC + manifest entered' },
  { id: 'holding', phase: 'launch', label: 'Holding page → real project (coming-soon off)' },
];

/** The existing state items 1 + 9 roll up (so we never re-implement either). */
export interface GoLiveRollups {
  /** The 27-viewport ResponsiveChecklist is complete (every viewport ticked). */
  responsiveDone: boolean;
  /** The client_approved gate (PR5) is satisfied. */
  clientApproved: boolean;
}

/** An item counts as satisfied when done OR N/A (manual), or when its roll-up is satisfied. */
export function itemSatisfied(item: GoLiveItem, marks: GoLiveMarks, rollups: GoLiveRollups): boolean {
  if (item.derived === 'responsive') return rollups.responsiveDone;
  if (item.derived === 'approved') return rollups.clientApproved;
  const s = marks[item.id];
  return s === 'done' || s === 'na';
}

export interface GoLiveProgress {
  satisfied: number;
  total: number;
  open: number;
}

/** Progress across all 15 items (derived + manual). open = still ☐ (neither done nor N/A). */
export function goliveProgress(marks: GoLiveMarks, rollups: GoLiveRollups): GoLiveProgress {
  let satisfied = 0;
  for (const it of GOLIVE_ITEMS) if (itemSatisfied(it, marks, rollups)) satisfied += 1;
  return { satisfied, total: GOLIVE_ITEMS.length, open: GOLIVE_ITEMS.length - satisfied };
}
